import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useI18n } from "@/i18n";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PromptBlock from "@/components/PromptBlock";
import useSeo from "@/lib/useSeo";
import { ArrowLeft, ArrowRight, AlertTriangle } from "lucide-react";

export default function PublicLesson() {
  const { lessonId } = useParams();
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [lesson, setLesson] = useState(null);

  const load = useCallback(async () => {
    setLesson(null);
    try {
      const { data } = await api.get(`/lessons/${lessonId}?lang=${lang}`);
      setLesson(data);
    } catch {
      setLesson(false);
    }
    window.scrollTo(0, 0);
  }, [lessonId, lang]);

  useEffect(() => { load(); }, [load]);

  const desc = lesson && lesson.content ? String(lesson.content.intro || "").slice(0, 155) : "";
  useSeo(lesson ? `${lesson.title} — AI Lernwerk` : "AI Lernwerk", desc);

  if (lesson === null) {
    return (
      <div className="min-h-screen bg-void">
        <Navbar />
        <div className="pt-40 text-center text-ink-soft" data-testid="public-lesson-loading">{t("common.loading")}</div>
      </div>
    );
  }
  if (lesson === false) {
    return (
      <div className="min-h-screen bg-void">
        <Navbar />
        <div className="pt-40 text-center text-ink-soft" data-testid="public-lesson-notfound">
          <p className="mb-6">404</p>
          <Link to="/curriculum" className="text-gold hover:text-gold-hover">{t("pl.back")}</Link>
        </div>
      </div>
    );
  }

  const c = lesson.content;

  return (
    <div className="min-h-screen bg-void">
      <Navbar />
      <main className="pt-28 pb-24 max-w-2xl mx-auto px-6" data-testid="public-lesson-page">
        <Link to={`/curriculum#${lesson.module_id}`} className="text-gold text-xs tracking-[0.3em] uppercase hover:text-gold-hover transition-colors">
          {lesson.module_title}
        </Link>
        <h1 className="font-serif text-4xl md:text-5xl text-ink leading-tight mt-3 mb-4">{lesson.title}</h1>
        <p className="text-ink-muted text-sm mb-8" data-testid="public-lesson-free-note">{t("pl.free")}</p>

        <motion.article initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}
          className="prose-editorial space-y-8">
          <p className="text-ink text-xl leading-relaxed">{c.intro}</p>

          {c.sections?.map((s, i) => (
            <div key={i}>
              <h2 className="font-serif text-2xl text-ink mb-3">{s.heading}</h2>
              <p className="text-ink-soft leading-relaxed whitespace-pre-line">{s.body}</p>
            </div>
          ))}

          {c.examples?.length > 0 && (
            <div>
              <h2 className="font-serif text-2xl text-ink mb-3">{t("lesson.examples")}</h2>
              <ul className="space-y-3">
                {c.examples.map((ex, i) => (
                  <li key={i} className="text-ink-soft leading-relaxed pl-4 border-l border-gold/30">{ex}</li>
                ))}
              </ul>
            </div>
          )}

          {c.prompts?.map((p, i) => <PromptBlock key={i} label={p.label} text={p.text} />)}

          {c.exercise && (
            <div className="bg-graphite border border-ink/5 p-6">
              <h2 className="font-serif text-2xl text-ink mb-3">{t("lesson.exercise")}</h2>
              <p className="text-ink-soft leading-relaxed">{c.exercise}</p>
            </div>
          )}

          {c.mistakes?.length > 0 && (
            <div>
              <h2 className="font-serif text-2xl text-ink mb-3 flex items-center gap-2">
                <AlertTriangle size={20} strokeWidth={1.5} className="text-gold-muted" /> {t("lesson.mistakes")}
              </h2>
              <ul className="space-y-2">
                {c.mistakes.map((m, i) => <li key={i} className="text-ink-soft leading-relaxed">— {m}</li>)}
              </ul>
            </div>
          )}

          {c.takeaway && (
            <div className="border-l-2 border-gold pl-6 py-2">
              <div className="text-[10px] tracking-[0.3em] uppercase text-gold-muted mb-2">{t("lesson.takeaway")}</div>
              <p className="font-serif text-2xl text-ink italic leading-snug">{c.takeaway}</p>
            </div>
          )}
        </motion.article>

        {/* Register CTA — substantial content precedes this */}
        <div className="mt-14 border border-gold/20 bg-graphite/60 p-8 text-center" data-testid="public-lesson-cta">
          <h2 className="font-serif text-2xl text-ink mb-2">{t("pl.cta_title")}</h2>
          <p className="text-ink-soft mb-6 max-w-md mx-auto">{t("pl.cta_body")}</p>
          <Link to="/register" className="inline-flex items-center gap-2 bg-gold text-void px-7 py-3 font-medium hover:bg-gold-hover transition-colors">
            {t("pl.cta_btn")} <ArrowRight size={18} strokeWidth={1.5} />
          </Link>
        </div>

        <nav className="flex justify-between items-center border-t border-ink/5 pt-6 mt-10" data-testid="public-lesson-nav">
          {lesson.prev ? (
            <button onClick={() => nav(`/learn/${lesson.prev.lesson_id}`)} data-testid="public-lesson-prev"
              className="flex items-center gap-2 text-ink-soft hover:text-gold transition-colors text-left">
              <ArrowLeft size={18} strokeWidth={1.5} />
              <span className="max-w-[9rem] truncate text-sm">{lesson.prev.title}</span>
            </button>
          ) : <span />}
          {lesson.next ? (
            <button onClick={() => nav(`/learn/${lesson.next.lesson_id}`)} data-testid="public-lesson-next"
              className="flex items-center gap-2 text-ink-soft hover:text-gold transition-colors text-right">
              <span className="max-w-[9rem] truncate text-sm">{lesson.next.title}</span>
              <ArrowRight size={18} strokeWidth={1.5} />
            </button>
          ) : <span />}
        </nav>
      </main>
      <Footer />
    </div>
  );
}
